<script lang="ts">
    import {onDestroy, onMount} from 'svelte';
    import {pipe} from 'core/utils';
    import {KpSearchItemAuthorityPresenter} from './kp-search-item-authority.presenter';
    import KpCover from '../../../record/kp-cover/KpCover.svelte';
    import {loc} from 'shared/utils/pipes';
    import KpRecordClipboardButton
        from '../../../record/kp-record-clipboard/kp-record-clipboard-button/KpRecordClipboardButton.svelte';
    import type {Rec} from 'typings/portaro.be.types';
    import KpCustomParagraph
        from 'shared/value-editors/internal/editors/search-or-edit/templates/KpCustomParagraph.svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {fade} from 'svelte/transition';
    import KpCollapsibleMenuWrapper from 'shared/ui-widgets/menu-wrapper/KpCollapsibleMenuWrapper.svelte';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {anyMatches, byIdOf} from 'shared/utils/array-utils';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';

    export let record: Rec;

    const localize = getLocalization();
    const presenter = getInjector().getByToken<KpSearchItemAuthorityPresenter>(KpSearchItemAuthorityPresenter.presenterName);
    let clipBoardRecords: Rec[] = [];
    let relatedRecordsCount: number;

    const recordsSubscription = presenter.clipboardRecords().subscribe((updatedRecordList) => clipBoardRecords = updatedRecordList);

    $: clipboardSize = clipBoardRecords.length;
    $: isClipboardNotEmpty = clipBoardRecords.length > 0;
    $: isNotIncludedInClipboard = !anyMatches(clipBoardRecords, byIdOf(record));

    onMount(async () => {
        relatedRecordsCount = await presenter.getRelatedRecordsCount(record);
    })
    onDestroy(() => recordsSubscription.unsubscribe());

    const handleEditClick = async () => {
        await presenter.createRecordEditation(record, record.fond);
    }
</script>

<KpCollapsibleMenuWrapper>
    <div slot="main" class="autoritaListItem"
         class:with-image-autorita={record?.cover}
         class:without-image-autorita={!record?.cover}
         class:no-related-documents-record={relatedRecordsCount === 0}>

        {#if record.cover}
            <div class="list-item-authority-left">
                <div class="cover-container cover-container-authority-search">
                    <KpCover record={record} href={presenter.getRecordLink(record.id)} fillTo='width'/>
                </div>
            </div>
        {/if}
        <div class="list-item-authority-main" class:with-image-list={record.cover} class:without-image-list={!record.cover}>
            <div class="list-item-authority-main-content">
                <div>
                    <h2 class="unset-style nazevZaznamu">
                        <a href={presenter.getRecordLink(record.id)}>{record.name}</a>
                    </h2>
                    {#if exists(relatedRecordsCount)}
                        <span class="dodatkovyText" title={localize(/* @kp-localization rejstrik.pocetDokumentuObsahujicichTutoAutoritu */ 'rejstrik.pocetDokumentuObsahujicichTutoAutoritu')}>
                            ({relatedRecordsCount}×)
                        </span>
                    {/if}
                    <KpCustomParagraph content={record.exports.portaroSearchItemParagraph} customClass="list-item-authority-paragraph-template"/>
                </div>
                <!-- alternative names -->
                <div>
                    {#if Array.isArray(record.alternativeNames)}
                        {#each record.alternativeNames as alternativeName}
                            <div style="margin-left: 15px;">
                                {alternativeName}
                            </div>
                        {/each}
                    {/if}
                </div>
                <!--VIZ TEZ AUTORITY-->
                <div>
                    {#if Array.isArray(record.seeAlsos)}
                        {#each record.seeAlsos as seeAlso}
                            <span style="margin-left: 15px;">
                                {localize(/* @kp-localization commons.vizTez */ 'commons.vizTez')}
                                <a href={presenter.getRecordLink(seeAlso.id)}>{pipe(seeAlso, loc())}</a>
                            </span>
                        {/each}
                    {/if}
                </div>
            </div>
        </div>
    </div>
    <svelte:fragment slot="menu" let:collapsed>
        {#if presenter.currentAuth.role.includes('ROLE_LIBRARIAN')}
            <li class="collapsible-menu-item">
                <div class="jp-clipboard-button-container everbis-hidden">
                    <KpRecordClipboardButton record={record}>
                        <svelte:fragment slot="add">
                            <UIcon icon="circle"/>
                            <span class:sr-only={collapsed}>{localize(/* @kp-localization commons.Mark */ 'commons.Mark')}</span>
                        </svelte:fragment>
                        <svelte:fragment slot="remove">
                            <UIcon icon="check-circle"/>
                            <span class:sr-only={collapsed}>{localize(/* @kp-localization commons.Marked */ 'commons.Marked')}</span>
                        </svelte:fragment>
                    </KpRecordClipboardButton>
                </div>
            </li>

            {#if presenter.currentAuth.role.includes('ROLE_LIBRARIAN') && isClipboardNotEmpty && isNotIncludedInClipboard}
                <li transition:fade|global={{duration:200}} class="collapsible-menu-item">
                    <div class="everbis-hidden">
                        <button class="btn btn-xs btn-default btn-block" on:click={() => presenter.mergeToThis(record)}>
                            <UIcon icon="file-import"/>
                            <span class:sr-only={collapsed}>{localize(/* @kp-localization record.MergeSelectedToThis */ 'record.MergeSelectedToThis')} ({clipboardSize})</span>
                        </button>
                    </div>
                </li>
            {/if}

            {#if presenter.currentAuth.role.includes('ROLE_LIBRARIAN')}
                <li class="collapsible-menu-item">
                    <div class="everbis-hidden">
                        <KpButton buttonStyle="primary"
                                  buttonSize="xs"
                                  isBlock
                                  on:click={handleEditClick}>

                            <IconedContent icon="edit">
                                <span class:sr-only={collapsed}>{localize(/* @kp-localization commons.editovat */ 'commons.editovat')}</span>
                            </IconedContent>
                        </KpButton>
                    </div>
                </li>
            {/if}
        {/if}
    </svelte:fragment>
</KpCollapsibleMenuWrapper>


<style lang="less">
    @import (reference) "../../../../shared/ui-widgets/collapsible-button-menu/collapsible-menu.constants.less";

    .cover-container-authority-search {
        float: left;
        width: 94px;
    }

    .with-image-autorita {
        grid-template-columns: 102px auto;
    }

    .without-image-autorita {
        grid-template-columns: auto;
    }

    .no-related-documents-record {
        opacity: 0.5;
    }

    .autoritaListItem {
        display: grid;
        grid-template-rows: 1fr;
        position: relative;
        justify-items: end;

        .with-image-list {
            grid-column: 2 / span 1;
        }

        .without-image-list {
            grid-column: 1 / span 2;
        }

        .list-item-authority-main {
            grid-row: 1;
            width: 100%;

            .list-item-authority-main-content {
                margin-right: @collapsed-width;
            }
        }

        .list-item-authority-left {
            grid-column: 1 / span 1;
            margin-right: 8px;
            justify-self: left;
        }
    }
</style>